"use client";

import { lazy, Suspense, useMemo, useState, type FormEvent } from "react";
import type { NumerologyRequest, NumerologyResponse } from "@/lib/api/numerology";
import { trackCalculatorUsed } from "@/lib/analytics-free-tools";
import { anchorResultSwapBeforeCommit } from "@/lib/result-swap-anchor";
import { toolErrorMessage } from "@/lib/tool-error-message";
import { NUMEROLOGY_COPY, type NumerologyCopy } from "./numerology.copy";

const loadResults = () => import("./NumerologyResults");
const NumerologyResults = lazy(loadResults);

export default function NumerologyClient({
  defaultLanguage = "en",
  sourcePath,
}: {
  defaultLanguage?: "en" | "hi";
  /** Canonical route identity for result-offer attribution. */
  sourcePath: string;
}) {
  const [name, setName] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<NumerologyResponse | null>(null);
  const copy = NUMEROLOGY_COPY[defaultLanguage];

  // Cap date picker at today — no future-birth numerology.
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const data: NumerologyRequest = {
        name: name.trim(),
        birth_date: birthDate,
      };
      const { calculateNumerology } = await import("@/lib/api/numerology");
      // Load the result island while the calculation runs. Keep the form's
      // loading state until both are ready, then anchor the one result swap.
      const [response] = await Promise.all([calculateNumerology(data), loadResults()]);
      anchorResultSwapBeforeCommit();
      setResult(response);
      trackCalculatorUsed({ calculator: "numerology" });
    } catch (err) {
      setError(toolErrorMessage(err, defaultLanguage, copy.error));
    } finally {
      setLoading(false);
    }
  }

  return (
    // Keep the calibrated form/result reserve outside the lazy result island.
    <div className="min-h-screen">
      {!result && (
        <NumerologyForm
          copy={copy}
          name={name}
          birthDate={birthDate}
          todayIso={todayIso}
          loading={loading}
          error={error}
          onNameChange={setName}
          onBirthDateChange={setBirthDate}
          onSubmit={handleSubmit}
        />
      )}
      {result && (
        <Suspense
          fallback={
            <p role="status">
              {defaultLanguage === "hi" ? "आपके अंक दिखाए जा रहे हैं…" : "Loading your numbers…"}
            </p>
          }
        >
          <NumerologyResults
            result={result}
            sourcePath={sourcePath}
            language={defaultLanguage}
            name={name}
            birthDate={birthDate}
            onReset={() => {
              setResult(null);
              setError(null);
              setName("");
              setBirthDate("");
            }}
          />
        </Suspense>
      )}
    </div>
  );
}

function NumerologyForm({
  copy,
  name,
  birthDate,
  todayIso,
  loading,
  error,
  onNameChange,
  onBirthDateChange,
  onSubmit,
}: {
  copy: NumerologyCopy;
  name: string;
  birthDate: string;
  todayIso: string;
  loading: boolean;
  error: string | null;
  onNameChange: (value: string) => void;
  onBirthDateChange: (value: string) => void;
  onSubmit: (event: FormEvent) => void;
}) {
  return (
    <div className="mx-auto max-w-md">
      <form translate="no" onSubmit={onSubmit} className="card-sacred p-6 sm:p-8 space-y-5">
        <div>
          <label
            htmlFor="num-name"
            className="block text-body-small font-semibold mb-2"
            style={{ color: "var(--ish-dark)" }}
          >
            {copy.nameLabel}
          </label>
          <input
            id="num-name"
            type="text"
            required
            value={name}
            onChange={(event) => onNameChange(event.target.value)}
            placeholder={copy.namePlaceholder}
            className="input-sacred w-full rounded-lg px-4 py-3 text-body"
            style={{ color: "var(--ish-dark)" }}
            autoComplete="name"
            autoCapitalize="words"
          />
          <p className="mt-1.5 text-[11px]" style={{ color: "var(--ish-text-faint)" }}>
            {copy.nameHint}
          </p>
        </div>

        <div>
          <label
            htmlFor="num-date"
            className="block text-body-small font-semibold mb-2"
            style={{ color: "var(--ish-dark)" }}
          >
            {copy.dateLabel}
          </label>
          <input
            id="num-date"
            type="date"
            required
            value={birthDate}
            onChange={(event) => onBirthDateChange(event.target.value)}
            min="1900-01-01"
            max={todayIso}
            className="input-sacred w-full rounded-lg px-4 py-3 text-body"
            style={{ color: "var(--ish-dark)" }}
            autoComplete="bday"
          />
        </div>

        <FormError error={error} />
        <NumerologySubmitButton loading={loading} copy={copy} />
      </form>
    </div>
  );
}

function FormError({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <div
      role="alert"
      aria-live="polite"
      className="text-body-small rounded-lg p-3.5"
      style={{
        color: "var(--color-error-text)",
        backgroundColor: "var(--color-error-bg)",
        border: "1px solid var(--color-error-tint)",
      }}
    >
      {error}
    </div>
  );
}

function NumerologySubmitButton({ loading, copy }: { loading: boolean; copy: NumerologyCopy }) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="btn-sacred w-full font-semibold py-3.5 rounded-lg text-body"
    >
      {loading ? (
        <span className="flex items-center justify-center gap-2.5">
          <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
          {copy.calculating}
        </span>
      ) : (
        <>{copy.submit} &rarr;</>
      )}
    </button>
  );
}
